import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from "@tanstack/react-query";

import { loginAtom } from '../../lib/auth';
import { currentPracticeAtom } from '../../lib/practice';
import { fetch_one } from "../../lib/v31lib";

import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';

import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';

import {
  Button,
  Dt,
  Page,
  Typography
} from '../../components/v2/styled';
import { Confirm } from '../../components/v2/dialogs/Confirm';

import { Workshop } from '../../models/Workshop';

type Props = {}

export const SharedWorkshopDetail: FunctionComponent<Props> = ({}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();

  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["workshop_detail", objectId],
    queryFn: () =>
      fetch_one<Workshop>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'workshop',
          fields: [
            'id', 'start_time', 'subject', 'get_medium', 'coach|id/get_medium/full_name', 'end_time', 'location', 'location_name', 'location_street', 'location_number', 'location_zip', 'location_city',
            'location_lat', 'location_lng', 'description', 'max_subscribers', 'max_backups', 'get_subscriber_ids', 'get_backup_ids', 'cost', 'teaser', 'get_bg_medium', 'is_background_colour', 'background_colour'
          ],
          sub_fields: {
            'location': ['id', 'get_medium', 'name', 'is_online', 'is_physical', 'enabled', 'settings', 'street', 'number', 'zip', 'city']
          },
          id: objectId,
          filter: {
            search: '',
            advanced: {
              is_frontpage: 1
            }
          }
        },
        undefined
      ),
    enabled: !!objectId,
  });

  useEffect(() => {
    setObjectId(params.id);
  }, [params.id]);
  useEffect(() => {
    if (!!params.id) objectRefetch();
  }, [params.id]);

  const mutationChangeSubscription = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      objectRefetch();
      setConfirmOpen(false);
    },
  });

  const workshop = object;

  let term = t("client.dashboard.workshops.title", "Workshops");
  if (!!currentPractice?.settings?.workshops_plural) {
    term = currentPractice.settings.workshops_plural;
  }

  return <Page
    title={workshop?.subject || ''}
    titleCrumbs={[
      {
        title: term,
        path: "/workshops"
      }
    ]}
  >
    {!objectSuccess && <Box sx={{textAlign: 'center'}}>
      <CircularProgress />
    </Box>}
    {!!objectError && <Box sx={{textAlign: 'center'}}>
      <Typography sx={{fontStyle: 'italic', color: theme.palette.secondary.main}}>{t("general.no_results_found")}</Typography>
    </Box>}

    {!!workshop && <Grid container spacing={1}>
      {!!workshop.teaser && <Grid item xs={12}>
        <Typography>{workshop.teaser}</Typography>
      </Grid>}
      <Grid container item xs={12} md={4} sx={{alignContent: 'baseline'}}>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("coach.models.workshops.start_time")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography>{<Dt d={workshop.start_time} f="ccc DD t" />}</Typography>
        </Grid>

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("coach.models.workshops.end_time")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography>{<Dt d={workshop.end_time} f="ccc DD t" />}</Typography>
        </Grid>

        <Grid item xs={12} sx={{marginTop: 1}}>
          <Divider sx={{borderColor: theme.palette.tertiary?.main}} />
        </Grid>

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("client.workshops.location")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography>{!!workshop.location ? workshop.location.name : workshop.location_name}</Typography>
        </Grid>

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("client.workshops.address")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          {!!workshop.location?.id && <>
            <Typography>{[workshop.location.street, workshop.location.number].join(' ')}</Typography>
            <Typography>{[workshop.location.zip, workshop.location.city].join(' ')}</Typography>
          </>}
          {!workshop.location?.id &&<>
            <Typography>{[workshop.location_street, workshop.location_number].join(' ')}</Typography>
            <Typography>{[workshop.location_zip, workshop.location_city].join(' ')}</Typography>
          </>}
        </Grid>

        <Grid item xs={12} sx={{marginTop: 1}}>
          <Divider sx={{borderColor: theme.palette.tertiary?.main}} />
        </Grid>

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("workshop_detail.coach")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Box sx={{textAlign: 'center', width: 'auto', display: 'inline-block'}}>
            <Avatar
              src={workshop.coach_get_medium}
              sx={{
                width: 72,
                height: 72,
                border: '2px solid transparent',
                borderColor: theme.palette.primary.main,
                margin: 'auto'
              }}
            />
            <Typography>{workshop.coach_full_name}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sx={{marginTop: 1}}>
          <Divider sx={{borderColor: theme.palette.tertiary?.main}} />
        </Grid>

        {!!workshop.cost && workshop.cost !== 'undefined' && workshop.cost !== 'null' && <>
          <Grid item xs={6} sx={{marginTop: 1}}>
            <Typography sx={{
              color: theme.palette.primary.main,
              fontStyle: 'italic',
            }}>{t("client.workshops.cost")}</Typography>
          </Grid>
          <Grid item xs={6} sx={{marginTop: 1}}>
            <Typography>{workshop.cost} €</Typography>
          </Grid>

          <Grid item xs={12} sx={{marginTop: 1}}>
            <Divider sx={{borderColor: theme.palette.tertiary?.main}} />
          </Grid>
        </>}

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("coach.submenu.workshops.subscribers")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography>{(workshop.get_subscriber_ids || []).length}/{workshop.max_subscribers}</Typography>
        </Grid>

        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography sx={{
            color: theme.palette.primary.main,
            fontStyle: 'italic',
          }}>{t("client.workshops.subscribed_backup")}</Typography>
        </Grid>
        <Grid item xs={6} sx={{marginTop: 1}}>
          <Typography>{(workshop.get_backup_ids || []).length}/{workshop.max_backups}</Typography>
        </Grid>

        <Grid item xs={12} sx={{marginTop: 1}}>
          <Divider sx={{borderColor: theme.palette.tertiary?.main}} />
        </Grid>

        <Grid item xs={12} sx={{marginTop: 1}}>
          {(workshop.get_subscriber_ids || []).indexOf(login?.id || -1) > -1 && <>
            <Typography sx={{color: theme.palette.primary.main, fontWeight: 'bold'}}>{t("client.workshops.subscribed")}</Typography>
            <Button
              id="unsub"
              label={t("client.general.actions.workshop_unsubscribe")}
              startIcon={<UnsubscribeIcon />}
              contained
              onClick={() => {
                setConfirmOpen(true);
              }}
            />
          </>}
          {(workshop.get_subscriber_ids || []).indexOf(login?.id || -1) < 0 && <>
            {(workshop.get_backup_ids || []).indexOf(login?.id || -1) > -1 && <>
              <Typography sx={{color: theme.palette.primary.main, fontWeight: 'bold'}}>{t("client.workshops.subscribed_backup")}</Typography>
              <Button
                id="unsub2"
                label={t("client.general.actions.workshop_unsubscribe")}
                startIcon={<UnsubscribeIcon />}
                contained
                onClick={() => {
                  setConfirmOpen(true);
                }}
              />
            </>}
            {(workshop.get_backup_ids || []).indexOf(login?.id || -1) < 0 && <>
              {!login?.id && <Button
                id="sub_fake"
                label={t("client.general.actions.workshop_subscribe_not_logged_in")}
                startIcon={<PersonAddIcon />}
                contained
                onClick={() => {
                  navigate("/login");
                }}
              />}
              {!!login?.id && (workshop.max_subscribers || 0) > (workshop.get_subscriber_ids || []).length && <Button
                id="sub"
                label={t("client.general.actions.workshop_subscribe")}
                startIcon={<PersonAddIcon />}
                contained
                onClick={() => {
                  mutationChangeSubscription.mutate({
                    formData: {
                      object: 'workshop',
                      class_action: 'custom_api_change_subscription',
                      id: workshop.id,
                      user_id: login?.id,
                      sub: 1
                    }
                  });
                }}
              />}
              {!!login?.id && (workshop.max_subscribers || 0) <= (workshop.get_subscriber_ids || []).length && (workshop.max_backups || 0) > (workshop.get_backup_ids || []).length && <Button
                id="sub_backup"
                label={t("client.general.actions.workshop_subscribe")}
                startIcon={<GroupAddIcon />}
                contained
                onClick={() => {
                  mutationChangeSubscription.mutate({
                    formData: {
                      object: 'workshop',
                      class_action: 'custom_api_change_subscription',
                      id: workshop.id,
                      user_id: login?.id,
                      sub: 1
                    }
                  });
                }}
              />}
              {(workshop.max_subscribers || 0) <= (workshop.get_subscriber_ids || []).length && (workshop.max_backups || 0) <= (workshop.get_backup_ids || []).length && <>
                <Typography sx={{color: theme.palette.primary.main, fontWeight: 'bold'}}>{t("client.workshops.full")}</Typography>
              </>}
            </>}
          </>}

          <Button
            id="back"
            label={t("client.general.buttons.back")}
            text
            onClick={() => {
              navigate(-1);
            }}
            sx={{marginLeft: 1}}
          />
        </Grid>

      </Grid>
      <Grid container item xs={12} md={8}>
        <Grid item xs={12}>
          <Typography html>{workshop.description}</Typography>
        </Grid>
      </Grid>
    </Grid>}

    <Confirm
      open={confirmOpen}
      setOpen={setConfirmOpen}
      title={t("client.general.actions.workshop_unsubscribe")}
      content={t("general.confirm.workshop_unsubscribe")}
      execute={() => {
        if (!!workshop) mutationChangeSubscription.mutate({
          formData: {
            object: 'workshop',
            class_action: 'custom_api_change_subscription',
            id: workshop.id,
            user_id: login?.id,
            sub: 0
          }
        });
      }}
    />
  </Page>;
}
